import { Formik, Form, ErrorMessage, Field } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState, useCallback, useRef } from 'react';
import showToast from '@/components/ui/Toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { Button } from '@/components/shadcn/button';
import { useNavigate } from 'react-router-dom';
import communityService from '@/services/communityService';

interface PasswordValues {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

interface BankAccountValues {
  namaBank: string;
  nomorRekening: string;
  atasNama: string;
}

const passwordInitial: PasswordValues = {
  email: '',
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
};

const bankAccountInitial: BankAccountValues = {
  namaBank: '',
  nomorRekening: '',
  atasNama: '',
};

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required('Password saat ini wajib diisi'),
  newPassword: Yup.string()
    .required('Password baru wajib diisi')
    .min(8, 'Password minimal 8 karakter')
    .matches(/[A-Z]/, 'Password harus mengandung minimal 1 huruf kapital')
    .matches(/[a-z]/, 'Password harus mengandung minimal 1 huruf kecil')
    .matches(/[0-9]/, 'Password harus mengandung minimal 1 angka')
    .matches(/[^A-Za-z0-9]/, 'Password harus mengandung minimal 1 karakter khusus'),
  confirmNewPassword: Yup.string()
    .required('Konfirmasi password wajib diisi')
    .oneOf([Yup.ref('newPassword')], 'Konfirmasi password tidak cocok'),
});

const bankAccountSchema = Yup.object({
  namaBank: Yup.string().required('Nama bank wajib diisi'),
  nomorRekening: Yup.string().required('Nomor rekening wajib diisi'),
  atasNama: Yup.string().required('Nama pemilik rekening wajib diisi'),
});

const bankOptions = [
  { value: 'ID_MANDIRI', label: 'Mandiri' },
  { value: 'ID_BCA', label: 'BCA' },
  { value: 'ID_BRI', label: 'BRI' },
  { value: 'ID_BNI', label: 'BNI' },
  { value: 'ID_CIMB', label: 'CIMB Niaga' },
  { value: 'ID_DANAMON', label: 'Danamon' },
  { value: 'ID_PERMATA', label: 'Permata' },
  { value: 'ID_MEGA', label: 'Mega' },
  { value: 'ID_BTN', label: 'BTN' },
  { value: 'ID_BII', label: 'BII (Maybank)' },
  { value: 'ID_JAGO', label: 'Jago' },
  { value: 'ID_SEABANK', label: 'SeaBank Indonesia' },
];

const AkunTab = () => {
  const navigate = useNavigate();
  const [passwordValues, setPasswordValues] = useState<PasswordValues>(passwordInitial);
  const [bankAccountValues, setBankAccountValues] = useState<BankAccountValues>(bankAccountInitial);
  const mountedRef = useRef(true);

  const fetchCommunityAccount = useCallback(async () => {
    try {
      const account = await communityService.getAccount();
      if (!mountedRef.current) return;
      if (account?.email) {
        setPasswordValues((prev) => ({ ...prev, email: account.email }));
      }

      const bankDetails = await communityService.getBankDetails();
      if (!mountedRef.current) return;
      if (bankDetails) {
        setBankAccountValues({
          namaBank: bankDetails.bank_name || '',
          nomorRekening: bankDetails.bank_account_number || '',
          atasNama: bankDetails.bank_account_holder || '',
        });
      }
    } catch (_error) {
      // ignore
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchCommunityAccount();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchCommunityAccount]);

  const handlePasswordSubmit = async (
    values: PasswordValues,
    { setSubmitting, resetForm, setErrors }: any
  ) => {
    try {
      await communityService.updatePassword({
        current_password: values.currentPassword,
        new_password: values.newPassword,
        confirm_new_password: values.confirmNewPassword,
      });
      resetForm({
        values: {
          ...values,
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: '',
        },
      });
      showToast.success('Password berhasil diperbarui!');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Gagal memperbarui password';
      showToast.error(errorMessage);
      if (errorMessage.includes('Password lama')) {
        setErrors({ currentPassword: errorMessage });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBankAccountSubmit = async (
    values: BankAccountValues,
    { setSubmitting, setErrors }: any
  ) => {
    try {
      if (!values.namaBank || !values.nomorRekening || !values.atasNama) {
        setErrors({
          namaBank: !values.namaBank ? 'Nama bank wajib diisi' : undefined,
          nomorRekening: !values.nomorRekening ? 'Nomor rekening wajib diisi' : undefined,
          atasNama: !values.atasNama ? 'Nama pemilik rekening wajib diisi' : undefined,
        });
        showToast.error('Semua field harus diisi');
        setSubmitting(false);
        return;
      }
      await communityService.updateBankDetails({
        bank_name: values.namaBank,
        bank_account_number: values.nomorRekening,
        bank_account_holder: values.atasNama,
      });
      showToast.success('Informasi rekening bank berhasil diperbarui!');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || 'Gagal memperbarui informasi rekening bank';
      showToast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Formik
        initialValues={passwordValues}
        validationSchema={passwordSchema}
        onSubmit={handlePasswordSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Card>
              <CardHeader>
                <CardTitle>Email & Keamanan</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Field name="email">
                    {({ field }: any) => (
                      <Input
                        id="email"
                        type="email"
                        placeholder="email@example.com"
                        disabled
                        {...field}
                      />
                    )}
                  </Field>
                  <p className="text-xs text-muted-foreground">Email tidak dapat diubah</p>
                </div>

                <div className="border-t border-border my-4" />

                <h3 className="text-sm font-semibold">Update Password</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Password Saat Ini</Label>
                    <Field name="currentPassword">
                      {({ field }: any) => (
                        <Input
                          id="currentPassword"
                          type="password"
                          placeholder="Masukkan password saat ini"
                          {...field}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="currentPassword"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Password Baru</Label>
                    <Field name="newPassword">
                      {({ field }: any) => (
                        <Input
                          id="newPassword"
                          type="password"
                          placeholder="Masukkan password baru"
                          {...field}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="newPassword"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmNewPassword">Konfirmasi Password Baru</Label>
                    <Field name="confirmNewPassword">
                      {({ field }: any) => (
                        <Input
                          id="confirmNewPassword"
                          type="password"
                          placeholder="Konfirmasi password baru"
                          {...field}
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="confirmNewPassword"
                      component="div"
                      className="text-destructive text-xs"
                    />
                  </div>

                  <Card className="bg-muted/50 border-muted">
                    <CardContent className="p-3">
                      <p className="text-xs font-semibold text-foreground mb-1">
                        Syarat Password Kuat:
                      </p>
                      <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Minimal 8 karakter</li>
                        <li>Minimal 1 huruf kapital (A-Z)</li>
                        <li>Minimal 1 huruf kecil (a-z)</li>
                        <li>Minimal 1 angka (0-9)</li>
                        <li>Minimal 1 karakter khusus (!@#$%^&*)</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Mengirim...' : 'Update Password'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Form>
        )}
      </Formik>

      <Formik
        initialValues={bankAccountValues}
        validationSchema={bankAccountSchema}
        onSubmit={handleBankAccountSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>
            <Card>
              <CardHeader>
                <CardTitle>Informasi Rekening Bank</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Informasi rekening bank untuk menerima pembayaran dari event berbayar.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="namaBank">
                    Nama Bank<span className="text-destructive ml-1">*</span>
                  </Label>
                  <Field name="namaBank">
                    {({ field }: any) => (
                      <Select
                        value={field.value === '' ? undefined : field.value}
                        onValueChange={(v: string) => setFieldValue('namaBank', v)}
                      >
                        <SelectTrigger id="namaBank">
                          <SelectValue placeholder="Pilih bank" />
                        </SelectTrigger>
                        <SelectContent>
                          {bankOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                  <ErrorMessage
                    name="namaBank"
                    component="div"
                    className="text-destructive text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nomorRekening">
                    Nomor Rekening<span className="text-destructive ml-1">*</span>
                  </Label>
                  <Field name="nomorRekening">
                    {({ field }: any) => (
                      <Input id="nomorRekening" placeholder="Masukkan nomor rekening" {...field} />
                    )}
                  </Field>
                  <ErrorMessage
                    name="nomorRekening"
                    component="div"
                    className="text-destructive text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="atasNama">
                    Atas Nama<span className="text-destructive ml-1">*</span>
                  </Label>
                  <Field name="atasNama">
                    {({ field }: any) => (
                      <Input id="atasNama" placeholder="Nama pemilik rekening" {...field} />
                    )}
                  </Field>
                  <ErrorMessage
                    name="atasNama"
                    component="div"
                    className="text-destructive text-xs"
                  />
                </div>

                <Card className="bg-muted/50 border-muted">
                  <CardContent className="p-3">
                    <p className="text-xs text-muted-foreground">
                      <strong className="text-foreground">Penting:</strong> Pastikan informasi
                      rekening bank sudah benar. Digunakan untuk transfer pembayaran dari event
                      berbayar.
                    </p>
                  </CardContent>
                </Card>

                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Menyimpan...' : 'Simpan Informasi Bank'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Form>
        )}
      </Formik>

      <div className="lg:col-span-2">
        <button
          type="button"
          onClick={() => navigate('/host/profile-settings/delete')}
          className="w-full sm:w-auto text-sm font-bold text-red-500 underline underline-offset-4 active:opacity-70"
        >
          Hapus Akun
        </button>
      </div>
    </div>
  );
};

export default AkunTab;
